import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import { ArrowBack, Download, Visibility, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledTypography } from '../styles/styles';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ImpedanceGraph from '../../../components/CellInfo/ImpedanceGraph';
import CircuitParameters from '../../../components/CellInfo/CircuitParameters';
import BarcodeCard from '../../../components/Cards/BarcodeCard';
import CellImageCard from '../../../components/Cards/CellImageCard';
import HppcGraph from '../../../components/CellInfo/HppcGraph';
import ChargeDischargeGraph from '../../../components/CellInfo/ChargeDischargeGraph';
import CapacityVsCyclePlot from '../../../components/CellInfo/CapacityVsCyclePlot';
import InternalResistanceVsCyclePlot from '../../../components/CellInfo/InternalResistanceVsCyclePlot';
import CircuitParametersTable from '../../../components/CellInfo/CircuitParametersTable';
import { Loader } from '../../../components/Loaders/loader';
import { CircularProgress } from '@mui/material';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import CellHealthCertificatePDF from './CellHealthCertificatePDF';
import CircuitParameterConstant from '../../../components/CellInfo/CircuitParameterConstant';
import ChargeDischargePlot from '../../../components/CellInfo/ChargeDischargePlot';
import OriginalCertificate from '../../../assets/cert/pdf/certificate.pdf';
import CellImage1 from '../../../assets/cert/cellCertimg1.jpeg';
import CellImage2 from '../../../assets/cert/cellCertimg2.jpeg';
import CellImage3 from '../../../assets/cert/cellCertimg3.jpeg';
import BarcodeImage from '../../../assets/cert/barcode.png';
import EquivalentCircuitImage from '../../../assets/equivalentcircuit.png';
const staticCellId = '6697948e2f3802119cef0f21';

const CellHealthCertificate = () => {
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [cellData, setCellData] = useState({
    battery_id: '',
    meta: {},
    electrical_properties: {},
    mechanical_properties: {},
    cell_images: [],
    cell_csvs: [],
  });
  const [ImpedanceCsvData, setImpedanceCsvData] = useState('');
  const [HppcCsvData, setHppcCsvData] = useState('');
  const [CycleCsvData, setCycleCsvData] = useState('');
  const [CellImage, setCellImage] = useState('');
  const [circuitParameters, setCircuitParameters] = useState({});

  // Cell images for the slider
  const cellImages = [
    CellImage1,
    CellImage2,
    CellImage3
  ];

  useEffect(() => {
    const getCellData = async () => {
      try {
        const response = await axios.get(`${process.env.BACKEND_URL}batteries/id/${staticCellId}`);
        const data = response.data;
        setCellData(data);

        if (data.cell_csvs && data.cell_csvs.length > 0) {
          setImpedanceCsvData(data.cell_csvs[0]?.csv_path || '');
          setHppcCsvData(data.cell_csvs[1]?.csv_path || '');
          setCycleCsvData(data.cell_csvs[2]?.csv_path || '');
        }

        if (data.cell_images && data.cell_images.length > 0) {
          setCellImage(data.cell_images[0]?.image_path || '');
        }
      } catch (error) {
        console.error('Failed to fetch cell data:', error);
      }
    };
    getCellData();

    // Fetch circuit parameters for PDF
    const fetchCircuitParameters = async () => {
      try {
        const response = await axios.get(`${process.env.BACKEND_URL}batteries/circuit_parameters/${staticCellId}`);
        if (typeof response.data === 'object') {
          setCircuitParameters(response.data);
        }
      } catch (error) {
        setCircuitParameters({});
      }
    };
    fetchCircuitParameters();
  }, []);

  // Add print styles for PDF generation and scroll improvements
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .js-plotly-plot, .plotly {
          page-break-inside: avoid;
        }
        canvas {
          max-width: 100% !important;
          height: auto !important;
        }
      }
      
      /* Smooth scrolling for the certificate content */
      .certificate-scroll {
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }
      
      /* Show scrollbar always for better UX */
      .certificate-scroll::-webkit-scrollbar {
        width: 8px !important;
      }
      
      .certificate-scroll::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1) !important;
        border-radius: 4px !important;
      }
      
      .certificate-scroll::-webkit-scrollbar-thumb {
        background: #e8442d !important;
        border-radius: 4px !important;
      }
      
      .certificate-scroll::-webkit-scrollbar-thumb:hover {
        background: #ff6b35 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const downloadFile = async (file) => {
    const response = await axios.get(`${process.env.BACKEND_URL}files/${file}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file);
    document.body.appendChild(link);
    link.click();
  };

  const generatePDF = async () => {
    if (!certificateRef.current) return;

    // Check if all data is loaded
    if (!cellData.battery_id || !CycleCsvData) {
      alert('Please wait for all data to load before generating PDF.');
      return;
    }

    setIsGeneratingPDF(true);

    try {
      // Wait for any async operations to complete and ensure charts are rendered
      await new Promise(resolve => setTimeout(resolve, 2000));

      const element = certificateRef.current;
      // Save original styles
      const originalBorderRadius = element.style.borderRadius;
      const originalPadding = element.style.padding;
      const originalBackground = element.style.background;
      const originalOverflow = element.style.overflow;
      const originalHeight = element.style.height;
      // Remove border radius and padding for PDF
      element.style.borderRadius = '0';
      element.style.padding = '0';
      element.style.background = '#000';
      element.style.overflow = 'visible';
      element.style.height = 'auto';

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('[data-certificate-content]');
          if (clonedElement) {
            clonedElement.style.borderRadius = '0';
            clonedElement.style.padding = '0';
            clonedElement.style.background = '#000';
            clonedElement.style.overflow = 'visible';
            clonedElement.style.height = 'auto';
            clonedElement.style.boxSizing = 'border-box';
          }
        }
      });

      // Restore original styles
      element.style.borderRadius = originalBorderRadius;
      element.style.padding = originalPadding;
      element.style.background = originalBackground;
      element.style.overflow = originalOverflow;
      element.style.height = originalHeight;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      // Scale so the image width matches the PDF width
      const imgWidth = pdfWidth;
      const scale = imgWidth / imgWidthPx;
      const imgHeight = imgHeightPx * scale;

      let position = 0;
      let pageHeightLeft = imgHeight;
      let pageNum = 0;
      const pageHeight = pdfHeight;

      while (pageHeightLeft > 0) {
        if (pageNum > 0) pdf.addPage();
        // Calculate the portion of the image to draw
        const sourceY = (imgHeightPx * position) / imgHeight;
        const sourceHeight = (imgHeightPx * Math.min(pageHeight, pageHeightLeft)) / imgHeight;
        // Create a temporary canvas for this page
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = imgWidthPx;
        tempCanvas.height = sourceHeight;
        tempCtx.drawImage(
          canvas,
          0, sourceY, imgWidthPx, sourceHeight,
          0, 0, imgWidthPx, sourceHeight
        );
        const pageImgData = tempCanvas.toDataURL('image/png');
        pdf.addImage(
          pageImgData,
          'PNG',
          0,
          0,
          imgWidth,
          (sourceHeight * scale)
        );
        position += pageHeight;
        pageHeightLeft -= pageHeight;
        pageNum++;
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `CellHealthCertificate_${cellData.battery_id || 'unknown'}_${timestamp}.pdf`;

      pdf.save(filename);

      // Show success message
      alert('Certificate PDF generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again. Make sure all charts are loaded.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!cellData.meta && !cellData.electrical_properties) {
    return <Loader />;
  }

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%)',
      borderRadius: '2rem',
      p: 3,
      fontFamily: 'Bai Jamjuree',
      color: '#fff',
      height: '90vh',
      position: 'relative',
      border: '1px solid rgba(232, 68, 45, 0.2)',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>

      {/* PDF Viewer Mode - Full Screen */}
      {showPDFViewer ? (
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative'
        }}>
          {/* Header with Close Button */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2,
            pb: 2,
            borderBottom: '1px solid rgba(232, 68, 45, 0.2)'
          }}>
            <StyledTypography variant="h5" sx={{ color: '#e8442d', fontWeight: 700 }}>
              Certificate PDF Viewer
            </StyledTypography>
            <Button
              variant="contained"
              onClick={() => setShowPDFViewer(false)}
              sx={{
                background: 'linear-gradient(45deg, #e8442d, #ff6b35)',
                color: 'white',
                fontWeight: 'bold',
                borderRadius: '20px',
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Close Viewer
            </Button>
          </Box>
          
          {/* PDF iframe */}
          <Box sx={{ 
            flex: 1,
            border: '2px solid #e8442d', 
            borderRadius: '12px', 
            overflow: 'hidden',
            background: '#fff'
          }}>
            <iframe
              src={OriginalCertificate}
              width="100%"
              height="100%"
              style={{ 
                border: 'none',
                borderRadius: '10px'
              }}
              title="Cell Health Certificate PDF"
            />
          </Box>
        </Box>
      ) : (
        /* Main Content - Certificate View */
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflowY: 'auto',
        maxHeight: '80vh',
        position: 'relative',
        height: 'calc(90vh - 60px)', // Account for parent padding
        mb: 0.5 // Space for buttons
      }}>
        <Box
          ref={certificateRef}
          data-certificate-content
          className="certificate-scroll"
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%)',
            borderRadius: '2rem',
            p: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#e8442d',
              borderRadius: '4px',
              '&:hover': {
                background: '#ff6b35',
              },
            },
          }}
        >
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
            <StyledTypography variant="h4" sx={{ color: '#e8442d', fontWeight: 900, mb: 1, letterSpacing: 1 }}>
              Cell Health Certificate
            </StyledTypography>
            <StyledTypography variant="subtitle1" sx={{ color: '#cccccc', fontStyle: 'italic', letterSpacing: 1, mb: 2 }}>
              Comprehensive Analysis Report
            </StyledTypography>
            <Box sx={{
              width: '40px',
              height: '4px',
              background: 'linear-gradient(90deg, #e8442d, #ff6b35)',
              borderRadius: '2px',
              mx: 'auto',
              opacity: 0.7,
              mb: 3
            }} />
            
            {/* Certificate Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Visibility />}
                onClick={() => setShowPDFViewer(!showPDFViewer)}
                sx={{
                  background: 'linear-gradient(45deg, #2d7de8, #35a3ff)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  borderRadius: '25px',
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 8px 25px rgba(45, 125, 232, 0.4)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(45, 125, 232, 0.6)',
                  },
                }}
              >
                {showPDFViewer ? 'Hide PDF' : 'View PDF'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = OriginalCertificate;
                  link.download = 'CellHealthCertificate.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                sx={{
                  background: 'linear-gradient(45deg, #e8442d, #ff6b35)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  borderRadius: '25px',
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 8px 25px rgba(232, 68, 45, 0.4)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(232, 68, 45, 0.6)',
                  },
                }}
              >
                Download PDF
              </Button>
          </Box>
            </Box>



          {/* Top Row: Cell Image Slider, Barcode, Cell Information */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Cell Images Slider */}
              <Paper elevation={3} sx={{
                background: 'linear-gradient(135deg, #181818, #232323)',
                border: '2px solid #e8442d',
                borderRadius: '1rem',
                p: 2,
                minWidth: 300,
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 2, textAlign: 'center', fontWeight: 700 }}>
                  Cell Images
                </StyledTypography>
                
                {/* Image Display */}
                <Box sx={{ 
                  width: '100%', 
                  height: 250, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 2,
                  position: 'relative'
                }}>
                  <IconButton
                    onClick={() => setCurrentImageIndex((prev) => (prev - 1 + cellImages.length) % cellImages.length)}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      color: '#e8442d',
                      zIndex: 1,
                      '&:hover': { backgroundColor: 'rgba(232, 68, 45, 0.1)' }
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  
                  <Box
                    component="img"
                    src={cellImages[currentImageIndex]}
                    alt={`Cell Image ${currentImageIndex + 1}`}
                    sx={{
                      maxWidth: '80%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid rgba(232, 68, 45, 0.3)'
                    }}
                  />
                  
                  <IconButton
                    onClick={() => setCurrentImageIndex((prev) => (prev + 1) % cellImages.length)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      color: '#e8442d',
                      zIndex: 1,
                      '&:hover': { backgroundColor: 'rgba(232, 68, 45, 0.1)' }
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </Box>
                
                {/* 3-Dot Indicator */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  {cellImages.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: index === currentImageIndex ? '#e8442d' : 'rgba(232, 68, 45, 0.3)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: index === currentImageIndex ? '#ff6b35' : 'rgba(232, 68, 45, 0.5)',
                          transform: 'scale(1.2)'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* {cellData.battery_id && <BarcodeCard batteryId={cellData.battery_id} />} */}
              {/* Static Barcode Card */}
              <Paper elevation={3} sx={{
                background: 'linear-gradient(135deg, #181818, #232323)',
                border: '2px solid #e8442d',
                borderRadius: '1rem',
                p: 2,
                minWidth: 300,
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 2, textAlign: 'center', fontWeight: 700 }}>
                  Barcode
                </StyledTypography>
                
                <Box sx={{ 
                  width: '100%', 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flex: 1
                }}>
                  <Box
                    component="img"
                    src={BarcodeImage}
                    alt="Barcode"
                    sx={{
                      width: '75%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid rgba(232, 68, 45, 0.3)'
                    }}
                  />
                </Box>
              </Paper>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Paper elevation={3} sx={{
                background: 'linear-gradient(135deg, #181818, #232323)',
                border: '2px solid #e8442d',
                borderRadius: '1rem',
                p: 3,
                minWidth: 350,
                minHeight: 320
              }}>
                <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 3, textAlign: 'center', fontWeight: 700 }}>
                  Cell Information
                </StyledTypography>
                <Box sx={{
                  border: '1px solid rgba(232, 68, 45, 0.2)',
                  borderRadius: '0.5rem',
                  p: 2,
                  background: 'rgba(0,0,0,0.3)'
                }}>
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow sx={{ borderBottom: '1px solid rgba(232, 68, 45, 0.2)' }}>
                              <TableCell sx={{
                                color: '#e8442d',
                                fontWeight: 700,
                                borderRight: '1px solid rgba(232, 68, 45, 0.2)',
                                pr: 2
                              }}>
                            Make/Model
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontWeight: 500, pl: 2 }}>
                            LG 21700
                              </TableCell>
                            </TableRow>
                        <TableRow sx={{ borderBottom: '1px solid rgba(232, 68, 45, 0.2)' }}>
                          <TableCell sx={{
                            color: '#e8442d',
                            fontWeight: 700,
                            borderRight: '1px solid rgba(232, 68, 45, 0.2)',
                            pr: 2
                          }}>
                            Nominal Capacity
                          </TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 500, pl: 2 }}>
                            5000 mAh
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderBottom: '1px solid rgba(232, 68, 45, 0.2)' }}>
                          <TableCell sx={{
                            color: '#e8442d',
                            fontWeight: 700,
                            borderRight: '1px solid rgba(232, 68, 45, 0.2)',
                            pr: 2
                          }}>
                            Current Capacity
                          </TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 500, pl: 2 }}>
                            4500 mAh
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderBottom: '1px solid rgba(232, 68, 45, 0.2)' }}>
                          <TableCell sx={{
                            color: '#e8442d',
                            fontWeight: 700,
                            borderRight: '1px solid rgba(232, 68, 45, 0.2)',
                            pr: 2
                          }}>
                            Chemistry
                          </TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 500, pl: 2 }}>
                            NMC
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderBottom: '1px solid rgba(232, 68, 45, 0.2)' }}>
                              <TableCell sx={{
                                color: '#e8442d',
                                fontWeight: 700,
                                borderRight: '1px solid rgba(232, 68, 45, 0.2)',
                                pr: 2
                              }}>
                            Nominal Voltage
                              </TableCell>
                              <TableCell sx={{ color: '#fff', fontWeight: 500, pl: 2 }}>
                            3.6 V
                              </TableCell>
                            </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Box>
          </Box>


          {/* SOH and Risk Score Cards (from sketch) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ px: 5, py: 2, border: '2px solid #e8442d', borderRadius: '1.5rem', background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: 180 }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', fontWeight: 900, letterSpacing: 1 }}>
                SOH = 90%
              </StyledTypography>
            </Paper>
            <Paper elevation={3} sx={{ px: 5, py: 2, border: '2px solid #e8442d', borderRadius: '1.5rem', background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: 180 }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', fontWeight: 900, letterSpacing: 1 }}>
                Risk Score = 4/10
              </StyledTypography>
            </Paper>
            <Paper elevation={3} sx={{ px: 5, py: 2, border: '2px solid #e8442d', borderRadius: '1.5rem', background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: 180 }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', fontWeight: 900, letterSpacing: 1 }}>
                RUL = 575 cycles
              </StyledTypography>
            </Paper>
          </Box>

          {/* Cycle Plots */}
            <Box sx={{ mb: 4, mt: 10 }}>
            <Paper elevation={3} sx={{
              background: 'black',
              border: '2px solid #e8442d',
              borderRadius: '1rem',
              p: 3,
              mb: 3
            }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 3, textAlign: 'center', fontWeight: 700 }}>
                Cell Aging Reference Data: Capacity vs Cycles
              </StyledTypography>
              <CapacityVsCyclePlot />
            </Paper>
            
            <Paper elevation={3} sx={{
              background: 'black',
              border: '2px solid #e8442d',
              borderRadius: '1rem',
              mt: 10,
              p: 3
            }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 3, textAlign: 'center', fontWeight: 700 }}>
                Cell Aging Reference Data: Internal Resistance vs Cycles
              </StyledTypography>
              <InternalResistanceVsCyclePlot />
            </Paper>
            </Box>


          {/* Charge/Discharge Plot */}
          <Box sx={{ mb: 4, mt: 10 }}>
            <Paper elevation={3} sx={{
              background: 'black',
              border: '2px solid #e8442d',
              borderRadius: '1rem',
              p: 3
            }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 3, textAlign: 'center', fontWeight: 700 }}>
                Charge/Discharge Curves for Different Cell States
              </StyledTypography>
              <ChargeDischargePlot />
            </Paper>
          </Box>

          {/* Circuit Parameters */}
          <Box sx={{ mb: 6, mt: 10 }}>
            {/* Equivalent Circuit Diagram */}
            <Paper elevation={3} sx={{
              background: 'transparent',
              border: '2px solid #e8442d',
              borderRadius: '1rem',
              p: 3,
              mb: 3
            }}>
              <StyledTypography variant="h6" sx={{ color: '#e8442d', mb: 4, textAlign: 'center', fontWeight: 700 }}>
                Equivalent Circuit Diagram
              </StyledTypography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={EquivalentCircuitImage}
                  alt="Equivalent Circuit Diagram"
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px'
                  }}
                />
            </Box>
            </Paper>
            
            {/* Circuit Parameters Table */}
            <CircuitParametersTable />
            </Box>

          {/* End of Certificate marker with minimal spacing */}
          <StyledTypography variant="body2" sx={{ color: '#cccccc', textAlign: 'center', mb: 2 }}>
            End of Certificate
          </StyledTypography>
        </Box>

        </Box>
      )}
    </Box>
  );
};

export default CellHealthCertificate;
